<script setup lang="ts">
import { computed, ref, watch, type VNodeChild } from 'vue'
import { X } from '@lucide/vue'
import RrButton from '../button/RrButton.vue'
import RenderContent from '../internal/RenderContent'
import { useScrollLock } from '../../composables/useScrollLock'
import { useOverlayFocusTrap } from '../../composables/useOverlayFocusTrap'
import { useUiConfig } from '../config-provider/context'
import type { ButtonType, DrawerPlacement } from '../../types'

import type { DrawerProps } from './types'

const props = withDefaults(defineProps<DrawerProps>(), { open: false, getContainer: 'body', footer: undefined, title: '', description: '', width: 378, height: 378, placement: 'right', closable: true, mask: true, maskClosable: true, keyboard: true, destroyOnClose: false, forceRender: false, showFooter: false, confirmLoading: false, okType: 'primary', showCancel: true, showOk: true })
defineOptions({ inheritAttrs: false })
const emit = defineEmits<{ 'update:open': [open: boolean]; close: [event?: Event]; ok: [event: MouseEvent]; afterOpenChange: [open: boolean] }>()
const config = useUiConfig()
const isOpen = computed(() => props.open)
const rendered = ref(props.open || props.forceRender)
watch([isOpen, () => props.forceRender], ([open, force]) => { if (open || force) rendered.value = true }, { flush: 'sync' })
const container = computed(() => typeof props.getContainer === 'function' ? props.getContainer() : props.getContainer ?? 'body')
const drawerRef = ref<HTMLElement | null>(null)
useScrollLock(isOpen)
function handleClose(event?: Event) { if (!isOpen.value) return; emit('update:open', false); emit('close', event) }
const { zIndex } = useOverlayFocusTrap(isOpen, drawerRef, { closeOnEscape: handleClose, escapeEnabled: () => props.keyboard, zIndexToken: '--rr-z-drawer', zIndex: () => props.zIndex })
function afterLeave() {
  if (isOpen.value) return
  if (props.destroyOnClose && !props.forceRender) rendered.value = false
  emit('afterOpenChange', false)
}
const transitionName = computed(() => `rr-slide-${props.placement}`)
const computedDimensionStyle = computed(() => {
  const horizontal = props.placement === 'left' || props.placement === 'right'
  const value = horizontal ? props.width : props.height
  const length = typeof value === 'number' || /^\d+$/.test(value) ? `${value}px` : value
  return horizontal ? { width: `min(${length}, 100vw)` } : { height: `min(${length}, 100vh)` }
})
</script>

<template>
  <Teleport v-if="rendered" :to="container === false ? 'body' : container" :disabled="container === false">
    <div class="rr-drawer-root" :style="{ zIndex }">
      <Transition name="rr-fade" appear><div v-if="isOpen && mask" class="rr-drawer-mask" aria-hidden="true" @click="maskClosable && handleClose($event)" /></Transition>
      <Transition :name="transitionName" appear @after-enter="emit('afterOpenChange', true)" @after-leave="afterLeave">
        <aside v-show="isOpen" ref="drawerRef" v-bind="$attrs" class="rr-drawer" :class="`is-${placement}`" role="dialog" :aria-modal="mask || undefined" :aria-label="title || undefined" tabindex="-1" :style="computedDimensionStyle">
          <header v-if="title || $slots.title || description || closable || $slots.extra" class="rr-drawer__header">
            <div class="rr-drawer__title-area"><slot name="title"><h3 v-if="title" class="rr-drawer__title">{{ title }}</h3></slot><p v-if="description" class="rr-drawer__description">{{ description }}</p></div>
            <div class="rr-drawer__actions"><slot name="extra" /><button v-if="closable" type="button" class="rr-drawer__close-btn" data-overlay-close="true" title="关闭" aria-label="关闭" @click="handleClose"><X :size="17" aria-hidden="true" /></button></div>
          </header>
          <div class="rr-drawer__body"><slot /></div>
          <footer v-if="footer !== null && ($slots.footer || footer !== undefined || showFooter)" class="rr-drawer__footer">
            <slot name="footer">
              <RenderContent v-if="footer !== undefined" :value="footer" />
              <template v-else><RrButton v-if="showCancel" type="default" v-bind="cancelButtonProps" @click="handleClose">{{ cancelText ?? config.locale?.Modal?.cancelText ?? '取消' }}</RrButton><RrButton v-if="showOk" :type="okType" :loading="confirmLoading" v-bind="okButtonProps" @click="emit('ok', $event)">{{ okText ?? config.locale?.Modal?.okText ?? '确定' }}</RrButton></template>
            </slot>
          </footer>
        </aside>
      </Transition>
    </div>
  </Teleport>
</template>

<style scoped>
.rr-drawer-root {
  position: relative;
  z-index: var(--rr-z-drawer, 800);
}

.rr-drawer-mask {
  position: fixed;
  inset: 0;
  z-index: var(--rr-z-drawer, 800);
  background: rgba(15, 17, 21, 0.42);
  backdrop-filter: blur(2px);
}

.rr-drawer {
  position: fixed;
  z-index: var(--rr-z-drawer, 800);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background: var(--rr-color-surface);
  box-shadow: var(--rr-shadow-overlay);
  overflow: hidden;
}

.rr-drawer.is-right {
  top: 0;
  right: 0;
  bottom: 0;
  border-left: 1px solid var(--rr-color-border);
}

.rr-drawer.is-left {
  top: 0;
  left: 0;
  bottom: 0;
  border-right: 1px solid var(--rr-color-border);
}

.rr-drawer.is-top {
  top: 0;
  left: 0;
  right: 0;
  border-bottom: 1px solid var(--rr-color-border);
}

.rr-drawer.is-bottom {
  bottom: 0;
  left: 0;
  right: 0;
  border-top: 1px solid var(--rr-color-border);
}

.rr-drawer__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  min-height: 52px;
  padding: 14px 20px;
  border-bottom: 1px solid var(--rr-color-border);
}

.rr-drawer__title-area {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.rr-drawer__title {
  margin: 0;
  color: var(--rr-color-text-strong);
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
}

.rr-drawer__description {
  margin: 0;
  color: var(--rr-color-text-muted);
  font-size: 12px;
}

.rr-drawer__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rr-drawer__close-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  min-width: 32px !important;
  min-height: 32px !important;
  max-width: 32px !important;
  max-height: 32px !important;
  padding: 0 !important;
  margin: 0 !important;
  border: 0 !important;
  outline: 0;
  border-radius: var(--rr-radius-control);
  background: transparent !important;
  color: var(--rr-color-text-muted) !important;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  transition: background-color var(--rr-motion-duration-fast) var(--rr-motion-ease-out),
    color var(--rr-motion-duration-fast) var(--rr-motion-ease-out);
}

.rr-drawer__close-btn:hover {
  background: var(--rr-color-surface-subtle) !important;
  color: var(--rr-color-text-strong) !important;
}

.rr-drawer__body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 20px;
  overflow-y: auto;
  overscroll-behavior: contain;
  color: var(--rr-color-text);
  font-size: 13px;
}

.rr-drawer__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 20px;
  border-top: 1px solid var(--rr-color-border);
  background: var(--rr-color-surface-subtle);
}
</style>
